import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export interface OrganizationUrl {
  name: string;
  url: string;
}

export interface TherapyModality {
  id: string;
  name_en: string;
  name_cn: string;
  abbreviation: string | null;
  mechanism_en: string | null;
  mechanism_cn: string | null;
  procedures: string | null;
  session_length_minutes: number | null;
  recommended_number_of_sessions: number | null;
  certifications: string | null;
  organizations: string | null;
  evidence_level: string | null;
  stream_type: string | null;
  type: string | null;
  target_domain: string | null;
  subcategories: string[] | null;
  parent_therapy_id: string | null;
  parent_category: string | null;
  organization_urls: Json | null;
  is_parent_category: boolean;
  stage_0: boolean;
  stage_1: boolean;
  stage_2: boolean;
  stage_3: boolean;
  stage_4: boolean;
  is_active: boolean;
  sort_order: number;
}

// Helper to parse organization_urls safely
export const parseOrganizationUrls = (urls: Json | null): OrganizationUrl[] => {
  if (!urls || !Array.isArray(urls)) return [];
  return urls
    .filter((item): item is { name: string; url: string; [key: string]: Json } => 
      typeof item === 'object' && 
      item !== null && 
      'name' in item && 
      'url' in item &&
      typeof (item as Record<string, unknown>).name === 'string' &&
      typeof (item as Record<string, unknown>).url === 'string'
    )
    .map(item => ({ name: item.name as string, url: item.url as string }));
};

export interface HealingStage {
  id: number;
  stage_number: number;
  title_en: string | null;
  title_zh: string | null;
  subtitle_en: string | null;
  subtitle_zh: string | null;
  description: string | null;
  color_theme: string | null;
  is_active: boolean | null;
}

export const useTherapyModalities = () => {
  return useQuery({
    queryKey: ['therapy-modalities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('therapy_modalities')
        .select('*')
        .eq('is_active', true)
        .eq('is_parent_category', false) // Only show specific therapies, not parent categories
        .order('sort_order', { ascending: true })
        .order('name_en', { ascending: true });

      if (error) throw error;
      return data as TherapyModality[];
    },
  });
};

export const useHealingStages = () => {
  return useQuery({
    queryKey: ['healing-stages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('healing_stages')
        .select('*')
        .eq('is_active', true)
        .order('stage_number', { ascending: true });

      if (error) throw error;
      return data as HealingStage[];
    },
  });
};

// Get therapies grouped by stage
export const getTherapiesByStage = (therapies: TherapyModality[], stageNumber: number): TherapyModality[] => {
  const stageKey = `stage_${stageNumber}` as keyof TherapyModality;
  return therapies.filter(t => t[stageKey] === true);
};

// Get unique evidence levels
export const getEvidenceLevels = (therapies: TherapyModality[]): string[] => {
  const levels = new Set<string>();
  therapies.forEach(t => {
    if (t.evidence_level) levels.add(t.evidence_level);
  });
  return Array.from(levels).sort();
};

// Get unique stream types
export const getStreamTypes = (therapies: TherapyModality[]): string[] => {
  const types = new Set<string>();
  therapies.forEach(t => {
    if (t.stream_type) types.add(t.stream_type);
  });
  return Array.from(types).sort();
};
